import React, { PropsWithChildren } from 'react';
import Bugsnag, { type Event } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import ErrorPage from 'next/error';
import { NextPageContext } from 'next';

export const bugsnagEnabled = Boolean(process.env.NEXT_PUBLIC_BUGSNAG_API_KEY);

const THIRD_PARTY_SCRIPT_HOSTS = ['apps.bazaarvoice.com', 'gallery.sprinklr.com'];

const getHostname = (url?: string): string | null => {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

const FRAMELESS_NOISE_PATTERNS: RegExp[] = [
  // Bazaarvoice bv.js cache wrapper, e.g. "Error fetching from cache: TypeError: Load failed"
  /error fetching from cache/i,
  // Sprinklr embed's bundled Swiper, e.g. "n.swiper.addCallback is not a function"
  /swiper.*addcallback is not a function/i,
];

const hasNoFrames = (event: Event) => !event.errors[0]?.stacktrace?.length;

const IGNORED_ERRORS: Array<(event: Event) => boolean> = [
  // 1. React hydration errors (preserves legacy .some behavior)
  ({ errors }) =>
    errors.some(
      (err) =>
        err.errorMessage?.startsWith('Minified React error #418') ||
        err.errorMessage?.startsWith('Minified React error #423'),
    ),

  // 2. XHR blob responseText bug (preserves legacy .some behavior)
  ({ errors }) =>
    errors.some(
      (err) =>
        err.errorClass === 'InvalidStateError' &&
        err.errorMessage?.includes('responseText') &&
        err.errorMessage?.includes('blob'),
    ),

  // 3. Bazaarvoice analytics timeout (preserves legacy .some behavior)
  ({ errors }) =>
    errors.some(
      (err) =>
        err.errorClass === 'Error' &&
        err.errorMessage?.includes('script timeout') &&
        err.errorMessage?.includes('bv-analytics.js'),
    ),

  // 4. Third-party script error: checks top-level frame only (avoids dropping first-party causes)
  (event) => {
    const host = getHostname(event.errors[0]?.stacktrace?.[0]?.file);
    if (!host) return false;
    return THIRD_PARTY_SCRIPT_HOSTS.some(
      (target) => host === target || host.endsWith(`.${target}`),
    );
  },

  // 5. Frameless errors known to come from third-party scripts (message-based, narrow on purpose)
  (event) => {
    if (!hasNoFrames(event)) return false;
    const message = event.errors[0]?.errorMessage ?? '';
    return FRAMELESS_NOISE_PATTERNS.some((pattern) => pattern.test(message));
  },
];

let started = false;
if (!started && bugsnagEnabled) {
  started = true;
  Bugsnag.start({
    onError: (event) => !IGNORED_ERRORS.some((shouldIgnore) => shouldIgnore(event)),
    enabledReleaseStages: ['test', 'qa', 'preprod', 'production'],
    enabledBreadcrumbTypes: ['navigation', 'user', 'state', 'log', 'error', 'process'],
    apiKey: `${process.env.NEXT_PUBLIC_BUGSNAG_API_KEY}`,
    plugins: [new BugsnagPluginReact(React)],
    releaseStage: process.env.NODE_ENV,
  });
}

const Error = class Page extends React.Component<{
  error: Error;
  info: React.ErrorInfo;
  clearError: () => void;
}> {
  static async getInitialProps(ctx: NextPageContext) {
    if (ctx.err && bugsnagEnabled) Bugsnag.notify(ctx.err);
    return ErrorPage.getInitialProps(ctx);
  }
  render() {
    return <ErrorPage statusCode={500} />;
  }
};

const ErrorBoundary = ({ children }: PropsWithChildren) => {
  if (!bugsnagEnabled) {
    return <>{children}</>;
  }

  const BugsnagBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary();
  return <BugsnagBoundary FallbackComponent={Error}>{children}</BugsnagBoundary>;
};

export default ErrorBoundary;
